// =============================================================================
// SIREPPAR - Hook: con qué agrupación está trabajando el usuario
// =============================================================================
// Un usuario puede administrar más de una agrupación: su partido y, por ejemplo,
// una alianza que ese partido integra. Su cuenta es una sola (el DNI es único); lo
// que cambia es con cuál trabaja.
//
// El contexto vive en la SESIÓN del backend, no en el token ni acá: este hook lo
// lee y lo cambia, pero no lo guarda.
//
// Al cambiar se RECARGA la página a propósito. Todo lo que hay en pantalla —
// expedientes, documentos, autoridades, alertas— pertenece a la agrupación
// anterior, y una invalidación selectiva de react-query dejaría restos mezclados de
// las dos. Recargar es la única forma de garantizar que no quede nada de la otra.
//
// `cambiarA` acepta a dónde ir después del cambio: el selector del menú lleva al
// inicio, pero el botón del banner de la alianza lleva directo a Candidaturas, que
// es el único motivo por el que un partido pasa a administrar su alianza.
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export interface AgrupacionDelUsuario {
  partidoId: string;
  denominacion: string;
  sigla: string | null;
  /** ESTANDAR | CONFEDERACION | ALIANZA */
  clase: string;
  estadoPartido: string;
  esPrincipal: boolean;
  rol: string | null;
  activa: boolean;
}

const ROLES_INTERNOS = ['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'MESA_ENTRADAS', 'ANALISTA', 'AUDITOR'];

export function useAgrupaciones() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Los roles internos no pertenecen a ninguna agrupación: no tienen nada que elegir.
  const esExterno = !!user?.partidoId && !ROLES_INTERNOS.includes(user?.rol || '');

  const { data, isLoading } = useQuery({
    queryKey: ['mis-agrupaciones'],
    queryFn: async () => (await api.get('/auth/agrupaciones')).data.data as AgrupacionDelUsuario[],
    enabled: esExterno,
  });

  const agrupaciones = data ?? [];
  const activa = agrupaciones.find(a => a.activa) ?? null;

  const cambiar = useMutation({
    mutationFn: (vars: { partidoId: string; destino?: string }) =>
      api.post('/auth/contexto', { agrupacionId: vars.partidoId }),
    onSuccess: (_data, vars) => {
      queryClient.clear();
      window.location.assign(vars.destino ?? '/dashboard');
    },
  });

  return {
    isLoading,
    agrupaciones,
    activa,
    // El selector sólo tiene sentido con más de una: con una sola no hay nada que elegir.
    puedeCambiar: agrupaciones.length > 1,
    cambiarA: (partidoId: string, destino?: string) => cambiar.mutate({ partidoId, destino }),
    cambiando: cambiar.isPending,
  };
}
